import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, AlertTriangle, Scale, CreditCard, Ban, Shield, ArrowLeft } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Sun, Moon } from 'lucide-react';

const TermsConditions = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <>
      <Helmet>
        <title>Terms & Conditions | ParachPay</title>
        <meta name="description" content="ParachPay Terms and Conditions. Read our service agreement and usage policies." />
      </Helmet>
      
      <div className="min-h-screen bg-background">
        {/* Simple Header */}
        <header className="sticky top-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/images/parachpay-logo.jpg" 
                  alt="ParachPay Logo" 
                  width="40"
                  height="40"
                  className="h-10 w-auto"
                />
                <span className="font-bold text-xl tracking-wide">PARACHPAY</span>
              </Link>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={toggleTheme} data-testid="theme-toggle">
                  {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                </Button>
                <Link to="/">
                  <Button variant="ghost" className="gap-2">
                    <ArrowLeft className="w-4 h-4" />
                    Back
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="max-w-4xl mx-auto py-12 px-4">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/10 mb-4">
              <FileText className="w-8 h-8 text-emerald-600" />
            </div>
            <h1 className="font-heading text-4xl font-bold mb-4">Terms & Conditions</h1>
            <p className="text-muted-foreground">Last updated: March 2026</p>
          </div>

          {/* Legal Entity Notice */}
          <Card className="mb-8 border-emerald-500/20 bg-emerald-500/5">
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">
                ParachPay is operated by <strong>ASELU Investment Growth Limited</strong> (Nigeria, RC1947196), 
                registered office 67 Siluko Road, Benin City, Nigeria. 
                Contact: <a href="mailto:info@parachpay.com" className="text-emerald-500 hover:underline">info@parachpay.com</a>
              </p>
            </CardContent>
          </Card>

          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle>1. Agreement to Terms</CardTitle>
              </CardHeader>
              <CardContent className="prose dark:prose-invert max-w-none">
                <p>
                  By accessing or using ParachPay's payment gateway services ("Services"), you agree to be bound by these 
                  Terms and Conditions ("Terms"). If you do not agree to these Terms, you may not access or use our Services.
                </p>
                <p>
                  These Terms constitute a legally binding agreement between you ("User," "you," or "your") and ParachPay 
                ("Company," "we," "us," or "our"). Please read them carefully before using our Services.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Scale className="w-5 h-5 text-emerald-600" />
                2. Eligibility
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>To use ParachPay, you must:</p>
              <ul>
                <li>Be at least 18 years of age or the legal age of majority in your jurisdiction</li>
                <li>Have the legal capacity to enter into binding contracts</li>
                <li>Not be prohibited from using financial services under applicable laws</li>
                <li>Provide accurate and complete registration information</li>
                <li>Not have been previously suspended or removed from our Services</li>
              </ul>
              <p>
                By using our Services, you represent and warrant that you meet all eligibility requirements.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-emerald-600" />
                3. Account Registration and Security
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <h4>3.1 Account Creation</h4>
              <p>
                To access certain features, you must create an account. You agree to provide accurate, current, and 
                complete information during registration and to update such information to keep it accurate.
              </p>
              
              <h4>3.2 Account Security</h4>
              <p>You are responsible for:</p>
              <ul>
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Enabling and maintaining two-factor authentication (2FA)</li>
                <li>Immediately notifying us of any unauthorized use of your account</li>
              </ul>
              
              <h4>3.3 Identity Verification</h4>
              <p>
                We may require identity verification (KYC) to comply with anti-money laundering (AML) regulations. 
                Failure to complete verification may result in limited account functionality or suspension.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>4. Services and Fees</CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <h4>4.1 Payment Services</h4>
              <p>ParachPay provides the following services:</p>
              <ul>
                <li>Multi-currency wallet management (64 currencies supported)</li>
                <li>Domestic and international money transfers</li>
                <li>Currency exchange at competitive rates</li>
                <li>Virtual debit card issuance</li>
                <li>Payment request and QR code generation</li>
                <li>Scheduled and recurring transfers</li>
              </ul>
              
              <h4>4.2 Fees</h4>
              <p>Our standard fee structure includes:</p>
              <ul>
                <li><strong>Transfer Fee:</strong> 1.5% of the transaction amount</li>
                <li><strong>Currency Exchange Fee:</strong> 2.5% of the converted amount</li>
                <li><strong>Deposit Fee:</strong> 0.5% (varies by payment method)</li>
                <li><strong>Virtual Card:</strong> Free issuance, standard transaction fees apply</li>
              </ul>
              <p>
                Fees are subject to change. We will notify you of any fee changes at least 30 days in advance.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Ban className="w-5 h-5 text-red-600" />
                5. Prohibited Activities
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>You agree NOT to use ParachPay for:</p>
              <ul>
                <li>Illegal activities, including money laundering or terrorist financing</li>
                <li>Fraudulent transactions or identity theft</li>
                <li>Purchasing illegal goods or services</li>
                <li>Circumventing sanctions or embargoes</li>
                <li>Operating unlicensed money transmission businesses</li>
                <li>Gambling where prohibited by law</li>
                <li>Adult content or services where prohibited</li>
                <li>Harassment, abuse, or harm to others</li>
                <li>Attempting to breach our security systems</li>
                <li>Creating multiple accounts to evade restrictions</li>
              </ul>
              <p>
                Violation of these prohibitions may result in immediate account suspension, fund freezing, 
                and reporting to relevant authorities.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>6. Transaction Limits and Holds</CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                We may impose transaction limits based on your account verification level, transaction history, 
                and risk assessment. We reserve the right to:
              </p>
              <ul>
                <li>Place holds on transactions pending review</li>
                <li>Request additional documentation for large transactions</li>
                <li>Decline transactions that appear suspicious or violate our policies</li>
                <li>Reverse transactions in cases of fraud or error</li>
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
                7. Disclaimers and Limitations
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <h4>7.1 Service Availability</h4>
              <p>
                We strive for 99.9% uptime but do not guarantee uninterrupted access. Services may be temporarily 
                unavailable due to maintenance, updates, or circumstances beyond our control.
              </p>
              
              <h4>7.2 Exchange Rates</h4>
              <p>
                Exchange rates are provided for informational purposes and may fluctuate. The rate at the time 
                of transaction execution applies. We are not responsible for rate fluctuations.
              </p>
              
              <h4>7.3 Limitation of Liability</h4>
              <p>
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, PARACHPAY SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR GOODWILL.
              </p>
              <p>
                Our total liability for any claim shall not exceed the fees you paid to us in the 12 months 
                preceding the claim.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-emerald-600" />
                8. Dispute Resolution
              </CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <h4>8.1 Transaction Disputes</h4>
              <p>
                If you believe a transaction was made in error or without your authorization, you must notify us 
                within 60 days. We will investigate and may reverse unauthorized transactions.
              </p>
              
              <h4>8.2 Arbitration</h4>
              <p>
                Any disputes arising from these Terms shall be resolved through binding arbitration rather than 
                in court, except for claims that may be brought in small claims court. You waive any right to 
                participate in class action lawsuits.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>9. Termination</CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                You may close your account at any time by contacting support. We may suspend or terminate your 
                account if you:
              </p>
              <ul>
                <li>Violate these Terms or our policies</li>
                <li>Engage in prohibited activities</li>
                <li>Fail to complete required identity verification</li>
                <li>Have a negative balance for an extended period</li>
              </ul>
              <p>
                Upon termination, you must withdraw any remaining funds within 30 days, subject to any legal holds.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>10. Governing Law</CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                These Terms shall be governed by and construed in accordance with applicable financial services 
                regulations and the laws of the jurisdiction where ParachPay operates. You consent to the 
                exclusive jurisdiction of courts in that jurisdiction for any disputes.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>11. Changes to Terms</CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                We reserve the right to modify these Terms at any time. Material changes will be notified via email 
                or through our Services at least 30 days before taking effect. Continued use of our Services after 
                changes constitute acceptance of the modified Terms.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>12. WhatsApp Notifications</CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>
                If you choose to receive WhatsApp notifications, we use your WhatsApp number only to send you payment 
                updates through the WhatsApp Business Platform provided by Meta. You can opt out anytime in Settings.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>13. Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="prose dark:prose-invert max-w-none">
              <p>For questions about these Terms, please contact us:</p>
              <ul>
                <li><strong>Email:</strong> legal@parachpay.com</li>
                <li><strong>Support:</strong> support@parachpay.com</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-border text-center text-sm text-muted-foreground">
          <p>© 2026 ParachPay. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/privacy" className="hover:text-emerald-600 transition-colors">Privacy Policy</Link>
            <span>•</span>
            <Link to="/terms" className="hover:text-emerald-600 transition-colors">Terms & Conditions</Link>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default TermsConditions;
